import { useState, useEffect, useCallback } from 'react';

class TokenStorage {
  static tokenGlobalAlias = "s-token";
  static storageName = "app-auth";

  /**
   * Get token from local storage by storage name or global alias
   * if temp token is set, return temp token
   * @returns {(string | null)}
   */
  static get() {
    const appStorageToken = localStorage.getItem(TokenStorage.storageName);
    return localStorage.getItem(TokenStorage.tokenGlobalAlias) ?? appStorageToken ?? null;
  }

  /**
   * In this method, we rewrite token by global alias if it exists (s-token),
   * and set token to storage by storage name
   * @param {string} token - token
   * @returns {void}
   */
  static set(token) {
    let _token = token;
    const prevToken = localStorage.getItem(TokenStorage.tokenGlobalAlias);
    if (prevToken) {
      localStorage.setItem(TokenStorage.tokenGlobalAlias, _token);
    }
    localStorage.setItem(TokenStorage.storageName, token);
  }

  /**
   * Check if token exists in local storage
   * @returns {boolean}
   */
  static has() {
    return !!TokenStorage.get();
  }

  /**
   * Parse token from local storage
   * @returns {(ParsedJWT | null)}
   */
  static parse() {
    const token = TokenStorage.get();
    if (!token) return null;
    try {
      const parsedToken = JSON.parse(atob(token.split(".")[1]));
      return parsedToken;
    } catch (error) {
      return null;
    }
  }

  /**
   * Check if token is expired
   * @returns {boolean}
   */
  static isExpired() {
    const token = TokenStorage.parse();
    if (!token) return true;
    const tokenExp = token.exp * 1000; // PHP time in seconds to JS time in milliseconds
    return Date.now() >= tokenExp;
  }

  /**
   * Clear token from local storage
   * @returns {void}
   */
  static clear() {
    localStorage.removeItem(TokenStorage.storageName);
  }
}

/**
 * Get token for interceptor from Token Storage
 * @returns {string | null}
 */
const getToken = () => TokenStorage.get();

/**
 * Get profile id from token
 * @returns {number | null}
 */
const getProfileId = () => {
  const token = TokenStorage.parse();
  if (!token) return null;
  return token.profile;
};

/**
 * Fetches a token from the platform token API.
 * @param {string} requestUrl - Optional URL for token requests.
 * @param {number} platformId - Optional Platform ID for token requests.
 * @returns {Promise<GetAuthTokenResponse>} - Token response.
 */
const getAuthToken = async ({
  requestUrl,
  platformId
}) => {
  const isPlatformIdExist = typeof platformId === "number";
  const url = new URL(requestUrl ?? '');
  return fetch(`${url}${isPlatformIdExist ? `?id=${platformId}` : ""}`).then(res => res.json());
};

const useAuthToken = ({
  authenticateCallback,
  requestUrl,
  platformId,
  requestOnMount = false,
  tempToken
}) => {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [token, setToken] = useState(null);

  /**
   * Clear token on unmount
   */
  useEffect(() => {
    TokenStorage.clear();
  }, []);

  /**
   * Requests an authentication token from the API
   * @param {Object} options - Request options
   * @param {boolean} [options.persistToStorage=true] - Whether to persist token to storage
   * @param {({isLoggedIn: boolean, token: string} | undefined) => void} [options.onSuccess] - Success callback
   * @param {(error: AuthTokenError) => void} [options.onError] - Error callback
   * @param {() => void} [options.onSettled] - Callback called on completion regardless of outcome
   */
  const requestToken = useCallback(({
    persistToStorage = true,
    onSuccess,
    onError,
    onSettled
  } = {}) => {
    if (tempToken) {
      setToken(tempToken);
      TokenStorage.set(tempToken);
      setIsSuccess(true);
      onSuccess?.({
        isLoggedIn: true,
        token: tempToken
      });
      onSettled?.();
      return;
    }
    setIsLoading(true);
    getAuthToken({
      platformId,
      requestUrl
    }).then(response => {
      const token = response["s-token"];
      if (token) {
        setToken(token);
      }
      if (persistToStorage && token) {
        TokenStorage.set(token);
      }
      const isLoggedIn = !!TokenStorage.parse()?.profile;
      setIsSuccess(true);
      if (onSuccess) onSuccess({
        isLoggedIn,
        token
      });
    }).catch(error => {
      setIsError(true);
      if (onError) onError(error);
    }).finally(() => {
      setIsLoading(false);
      if (onSettled) onSettled();
    });
  }, [requestUrl, platformId, tempToken]);

  /**
   * Check if token is expired
   * @returns {boolean}
   */
  const shouldRequestNewToken = () => TokenStorage.isExpired();

  /**
   * Remove token from storage
   * @returns {void}
   */
  const removeToken = () => {
    TokenStorage.clear();
  };

  /**
   * Check if token is expired and user is logged in
   * @param {(string | null)} token - Token
   */
  useEffect(() => {
    if (!token) return;
    const isLoggedIn = TokenStorage.parse()?.profile;
    if (isLoggedIn) {
      authenticateCallback?.();
    }
  }, [token]);

  /**
   * Request token on mount
   * @param {boolean} requestOnMount - Request token on mount
   * @returns {void}
   */
  useEffect(() => {
    if (!requestOnMount) return;
    requestToken();
  }, []);
  return {
    requestToken,
    shouldRequestNewToken,
    removeToken,
    tokenFromStorage: TokenStorage.get(),
    requestedToken: token,
    isTokenExpired: TokenStorage.isExpired(),
    isTokenSuccess: isSuccess,
    isTokenError: isError,
    isTokenLoading: isLoading
  };
};

export { getProfileId, getToken, useAuthToken };
//# sourceMappingURL=index.es.js.map
